import { Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { lastValueFrom, Observable, of, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { RemoteLoggingService } from './remote-logging.service';
import { catchError, map } from 'rxjs/operators';

export class HttpDataService {
  protected apiUrl: string = environment.API_URL;
  protected baseUrl: string;
  protected http: HttpClient;
  protected log: RemoteLoggingService;
  protected headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(protected injector: Injector, protected apiEndPoint: string = '') {
    this.http = injector.get(HttpClient);
    this.log = injector.get(RemoteLoggingService);
    this.setBaseUrl(apiEndPoint);
  }

  setBaseUrl(apiEndPoint) {
    this.baseUrl = `${this.apiUrl}${apiEndPoint}`;
  }

  /**
   * Method to get the fetch url, usually the baseUrl, caution on changes it's overloaded in some subclasses
   * @param options options to build fetch url
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getFetchUrl(options?: any) {
    return this.baseUrl;
  }

  getItem(id): Observable<any> {
    const url = `${this.baseUrl}/${id}`;
    return this.fetchRequest(url, null, null);
  }

  create(props: any): Promise<any> {
    return this.postRequest(this.baseUrl, JSON.stringify(props), this.getRequestOptions());
  }

  update(obj: any, options?: any): Promise<any> {
    const url = `${this.baseUrl}/${obj.id}`;
    return this.putRequest(url, JSON.stringify(obj), this.getRequestOptions(options));
  }

  delete(id: string): Promise<any> {
    const url = `${this.baseUrl}/${id}`;
    return this.deleteRequest(url, this.getRequestOptions());
  }

  anonymize(id: String): Promise<any> {
    const url = `${this.baseUrl}/${id}/anonymize`;
    return this.putRequest(url, JSON.stringify({}), this.getRequestOptions());
  }

  fetchRequest(url, options, body?): Observable<any> {
    return this.http.get(url, this.getRequestOptions(options)).pipe(
      map((resp) => resp),
      catchError((error) => this.handleObservableError(error))
    );
  }

  postRequest(url, body, options): Promise<any> {
    return lastValueFrom(this.http.post(url, body, options))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  putRequest(url, body, options): Promise<any> {
    return lastValueFrom(this.http.put(url, body, options))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  deleteRequest(url, options): Promise<any> {
    return lastValueFrom(this.http.delete(url, options))
      .then((res) => true)
      .catch((error) => this.handlePromiseError(error));
  }

  fetch(options?: any): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(this.getFetchUrl(options), {
        ...this.getRequestOptions(options),
        observe: 'response'
      })
    )
      .then((response: HttpResponse<any>) => {
        switch (response.status) {
          case 200:
          case 201:
          case 202:
          case 203:
            return response.body;
          default:
            return false;
        }
      })
      .catch((error) => this.handleObservableError(error));
  }

  search(term: string, strict?: boolean, filters?: any): Observable<any> {
    if (strict && (!term || term.length < 3)) {
      return of(false);
    }
    return this.http
      .get<any>(this.getFetchUrl(), {
        ...this.getRequestOptions({ search: term, ...filters }),
        observe: 'response'
      })
      .pipe(
        map((response: HttpResponse<any>) => {
          switch (response.status) {
            case 200:
            case 201:
            case 202:
            case 203:
              return response.body;
            default:
              return false;
          }
        })
      );
  }

  protected getRequestOptions(options?: any): any {
    const requestOptions = {
      headers: this.headers,
      params: this.getQueryParams(options)
    };
    return requestOptions;
  }

  /**
   * Make the request from a map
   * @param options
   * @protected
   */
  protected getRequestOptionsMap(options?: Map<string, any[]>): any {
    return {
      headers: this.headers,
      params: this.getQueryParamMap(options)
    };
  }

  /**
   * Build a Params for the requests
   * @param options
   * @protected
   */
  protected getQueryParamMap(options: Map<string, any[]>): HttpParams {
    if (!options) {
      return null;
    }

    let params = new HttpParams();
    for (const [key, value] of options) {
      params = params.set(key, value.toString());
    }
    return params;
  }

  protected getQueryParams(options: any = {}) {
    if (!options) {
      return null;
    }
    return Object.keys(options).reduce((params, key) => {
      if (options[key]) {
        if (options[key] instanceof Array) {
          options[key].forEach(
            (value) => (params = params.has(key) ? params.append(key, value) : params.set(key, value))
          );
        } else {
          params = params.set(key, options[key]);
        }
      }
      return params;
    }, new HttpParams());
  }

  handlePromiseError(error: HttpErrorResponse) {
    return Promise.reject(this.handleError(error));
  }

  handleObservableError(error: HttpErrorResponse) {
    return throwError(() => this.handleError(error));
  }

  handleError(errorResponse: HttpErrorResponse) {
    const error = this.buildErrorObjectToDisplay(errorResponse);
    const errMsg = `${errorResponse.message}:\n${JSON.stringify(errorResponse.error, null, 2)}`;
    this.log.write(errMsg);
    return error;
  }

  buildErrorObjectToDisplay(errorResponse: HttpErrorResponse) {
    // TODO Each microservice send errors in a different way and here we must deal with all of them. Once the microservices are
    // standardized this part should be modified accordingly
    /* standardized error body sample:
            {
              "status": 400,
              "message": "User email already exists: user@email.com",
              "code": "USER_EMAIL_EXISTS",
              "payload": {
                "email": "user@email.com"
              }
            }
        */
    const responseData = errorResponse.error;
    const error = {
      responseStatusCode: responseData?.status || errorResponse.status,
      responseStatusMessage: responseData?.message || errorResponse.statusText,
      errorCode: responseData?.errorCode || 'X',
      fields: null,
      serverMessage: null,
      payload: responseData?.payload
    };

    const responseError = responseData?.error ? responseData.error : responseData;
    /*
        For testing specific errors, assign a value like this

        responseError = {
          "error": "ApiGames validation failed"
        };
        */

    if (responseError) {
      const fieldErrors = [];
      if (typeof responseError === 'string') {
        // The backend just sends a message indicating the error
        error.serverMessage = responseError;
        fieldErrors.push(this.guessFieldInError(responseError));
      } else if (responseError.errors) {
        // The backend sends an array containing each field in error
        for (const field in responseError.errors) {
          // eslint-disable-next-line no-prototype-builtins
          if (responseError.errors.hasOwnProperty(field)) {
            const err = responseError.errors[field];
            const fieldError = this.buildFieldErrorObject(err.path, err.kind, err.message, err.name);
            fieldErrors.push(fieldError);
          }
        }
        error.serverMessage = responseError.message;
      } else if (responseError.message) {
        // The backend just sends an object containing a "message" property
        error.serverMessage = responseError.message;
        fieldErrors.push(this.guessFieldInError(responseError.message));
      } else {
        error.serverMessage = 'Unknown error => ' + JSON.stringify(responseError);
      }
      error.fields = fieldErrors;
    } else {
      // future standard server message
      error.serverMessage = `error.${error.errorCode}`;
    }

    return error;
  }

  guessFieldInError(message: string): any {
    // Example of error message that we try to parse: Cast to number failed for value "asdf" at path "pid"

    const fieldError = {};

    const index = message.indexOf('path');
    if (index !== -1) {
      let field = message.slice(index + 6);
      field = field.slice(0, field.length - 1);

      let code = '';
      let type = '';

      const index1 = message.indexOf('Cast to');
      const index2 = message.indexOf('failed');
      if (index1 !== -1) {
        code = index1 !== -1 ? 'CastError' : 'Unknown';
        type = message.slice(index1 + 8, index2 - 1);
      } else {
        code = 'Unknown';
      }
      return this.buildFieldErrorObject(field, type, message, code);
    } else {
      return this.buildFieldErrorObject('*', 'Unknown', message, 'Unknown');
    }
  }

  buildFieldErrorObject(path, kind, message, name) {
    const fieldError = {
      path: '*',
      kind: 'Unknown',
      param: null,
      message: message
    };

    if (path) {
      fieldError.path = path;
    }
    if (kind) {
      fieldError.kind = kind;
    }

    if (name === 'CastError') {
      fieldError.kind = 'castError';
      fieldError.param = kind;
    }
    return fieldError;
  }
}
